import styled from '@emotion/styled';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isValidateCompanyRegistrationNumber } from '@utils/regex';
import { SignUpFormType } from '@components/_templates/auth/SignUpContent/types';

// components
import CompanyInfoBox from './CompanyInfoBox';
import Button from '@components/_atoms/Button';
import TextInputBox from '@components/_molecules/TextInputBox';
import { Toast } from '@components/_atoms/Toast';

// constants
import { ButtonColor, ButtonType, TextInputVariant } from '@constants/atoms';

// hooks
import { useValidCompanyRegistrationNumberFetch } from '@hooks/fetch/useAuthFetch';
import { useToggle } from '@hooks/toggle/useToggle';
import ButtonCode from '@constants/log/buttonCode';
import useGetBizCheckInfo, { useGetBizCheckInfoMutation } from '@hooks/fetch/spring/biz/useGetBizCheckInfo';

type Props = {
  data: CompanyRegistrationDataType;
  setData: Dispatch<SetStateAction<any>>;
  setIsValid: Dispatch<SetStateAction<boolean>>;
  form?: SignUpFormType;
  setForm?: Dispatch<SetStateAction<SignUpFormType>>;
  isUpload?: boolean;
};

export type CompanyRegistrationDataType = {
  company_registration_number: string;
  company_name: string;
  company_address: string;
  company_ceo: string;
};

const Layout = styled.div`
  display: grid;
  row-gap: 8px;
`;

// formatting
function __formatCompanyRegistrationNumber(input: string): string {
  const trimmedInput = input.replace(/[^0-9]/g, '');
  if (trimmedInput.length < 4) {
    return trimmedInput;
  } else if (trimmedInput.length < 7) {
    return `${trimmedInput.slice(0, 3)}-${trimmedInput.slice(3)}`;
  } else if (trimmedInput.length < 11) {
    return `${trimmedInput.slice(0, 3)}-${trimmedInput.slice(3, 5)}-${trimmedInput.slice(5)}`;
  } else {
    return `${trimmedInput.slice(0, 3)}-${trimmedInput.slice(3, 5)}-${trimmedInput.slice(5, 10)}-${trimmedInput.slice(
      10,
    )}`;
  }
}

export default function CompanyRegistrationNumber({ data, setData, setIsValid, form, setForm }: Props) {
  const { company_registration_number } = data;

  const openCompanyInfoBox = useToggle();

  const [errorMessage, setErrorMessage] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [isBizCheckInfoApiEnabled, setIsBizCheckInfoApiEnabled] = useState<boolean>(false);
  const isCompanyRegistrationNumberFile =
    form?.company_registration_number_file && form?.company_registration_number_file_name;

  useEffect(() => {
    if (isCompanyRegistrationNumberFile || disabled) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [errorMessage, disabled, isCompanyRegistrationNumberFile, setIsValid]);

  // 사업자등록번호 change
  const handleCompanyRegistrationNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    const companyRegstrationNumber = __formatCompanyRegistrationNumber(value);
    setData((prev: any) => ({
      ...prev,
      company_registration_number: companyRegstrationNumber,
    }));

    checkValidation(companyRegstrationNumber);
  };

  // check validation
  const checkValidation = (value: string) => {
    let message = '';

    if (!isValidateCompanyRegistrationNumber(value)) message = '형식에 맞지 않는 사업자등록번호에요.';
    setErrorMessage(message);
  };

  // 사업자등록번호 인증 popbill API
  const { mutate: bizCheckInfoMutate } = useGetBizCheckInfoMutation();

  const handleCompanyRegistrationNumberSubmit = async () => {
    if (errorMessage) {
      Toast.error('사업자등록번호를 확인해주세요.');
      return;
    }
    await bizCheckInfoMutate(company_registration_number, {
      onSuccess: (result) => {
        if (result?.data) {
          if (
            !result?.data['company_address'] ||
            !result?.data['company_ceo'] ||
            !result?.data['company_name'] ||
            !result?.data['company_registration_number']
          ) {
            setDisabled(false);
            openCompanyInfoBox.on();
          } else {
            setData((prev: any) => ({
              ...prev,
              ...result?.data,
            }));
            setDisabled(true);
            openCompanyInfoBox.on();
          }
        }
      },
      onError: (error) => {
        setDisabled(false);
        setIsBizCheckInfoApiEnabled(false);
        openCompanyInfoBox.on();
      },
    });
  };

  // 사업자등록번호 초기화
  const handleResetCompanyRegistrationNumber = () => {
    setData((prev: any) => ({
      ...prev,
      company_registration_number: '',
      company_registration_number_file_name: '',
      company_registration_number_file: '',
    }));
    setDisabled(false);
    openCompanyInfoBox.off();
  };

  return (
    <Layout>
      <TextInputBox
        title="사업자등록번호"
        textInput={{
          variant: errorMessage
            ? TextInputVariant.ERROR
            : disabled || isCompanyRegistrationNumberFile
            ? TextInputVariant.FIXED
            : TextInputVariant.DEFAULT,
          name: 'company_registration_number',
          value: company_registration_number,
          onChange: handleCompanyRegistrationNumberChange,
          placeholder: '사업자등록번호를 입력해 주세요.',
        }}
        errorMessage={!isCompanyRegistrationNumberFile ? errorMessage : ''}
        isRequired
      />
      <Button
        disabled={!company_registration_number || !!errorMessage}
        type={ButtonType.Button}
        width="100%"
        text="인증하기"
        color={ButtonColor.OutlinedBlue}
        onClick={handleCompanyRegistrationNumberSubmit}
        steelbosoId={ButtonCode.BTN_CERTIFICATION}
      />

      {/* Company Info Box */}
      {openCompanyInfoBox.isToggled && (
        <CompanyInfoBox
          data={data}
          handleResetCompanyRegistrationNumber={handleResetCompanyRegistrationNumber}
          disabled={disabled}
          form={form}
          setForm={setForm}
        />
      )}
      {/* Company Info Box end */}
    </Layout>
  );
}
